import { AnalyticsEvents, captureAnalyticsEvent, DEMO_BOOKING_URL, handleConsoleDemoNavigation, prepareConsoleDemoLink } from "@/analytics";
import { PublicShell } from "@/components/layout/PublicShell";
import { Button } from "@/components/ui/button.tsx";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card.tsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { useToast } from "@/components/ui/use-toast";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FaGithub } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { MdArrowBack, MdBusiness, MdEmail } from "react-icons/md";
import { PiMicrosoftOutlookLogoFill } from "react-icons/pi";
import { SiGmail } from "react-icons/si";
import { useSearchParams } from "react-router-dom";

type EmailFormData = {
  email: string;
};

type CodeFormData = {
  code: string;
};

type SSOFormData = {
  email: string;
};

const signupDomainErrorCode = "domain_not_allowed";
const ssoRequiredErrorCode = "sso_required";

export default function Login() {
  const [searchParams] = useSearchParams();
  const [isEmailMode, setIsEmailMode] = useState(false);
  const [isSSOMode, setIsSSOMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState("");
  const [isSignupDomainDialogOpen, setIsSignupDomainDialogOpen] = useState(false);
  const { toast } = useToast();
  const lastUsedAuthnProvider = localStorage.getItem("lastUsedAuthnProvider");

  // Handle invitation token from URL
  useEffect(() => {
    const invitationToken = searchParams.get("invitationToken");
    if (invitationToken) {
      localStorage.setItem("pendingInvitationToken", invitationToken);
    }

    // Handle magic link token from URL
    const token = searchParams.get("token");
    if (token) {
      // Redirect to magic link callback to process the token
      window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/magiclink/callback?token=${encodeURIComponent(token)}`;
    }

    if (searchParams.get("signupError") === signupDomainErrorCode) {
      setIsSignupDomainDialogOpen(true);
    }
    if ([ssoRequiredErrorCode, "scim_access_denied", "sso_access_denied", "sso_provider_error"].includes(searchParams.get("signupError") ?? "")) {
      setIsSSOMode(true);
      setIsEmailMode(false);
    }
  }, [searchParams]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EmailFormData>();

  const {
    register: registerCode,
    handleSubmit: handleSubmitCode,
    formState: { errors: codeErrors },
  } = useForm<CodeFormData>();
  const {
    register: registerSSO,
    handleSubmit: handleSubmitSSO,
    formState: { errors: ssoErrors },
  } = useForm<SSOFormData>();
  const handleGitHubLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "github");
    captureAnalyticsEvent(AnalyticsEvents.AUTH_STARTED, { provider: "github" });
    window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/github/login?${searchParams.toString()}`;
  };
  const handleGoogleLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "google");
    captureAnalyticsEvent(AnalyticsEvents.AUTH_STARTED, { provider: "google" });
    window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/google/login?${searchParams.toString()}`;
  };

  const handleAgentIDLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "agentid");
    captureAnalyticsEvent(AnalyticsEvents.AUTH_STARTED, { provider: "agentid" });
    window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/agentid/login?${searchParams.toString()}`;
  };

  const handleEmailLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "email");
    captureAnalyticsEvent(AnalyticsEvents.AUTH_STARTED, { provider: "email" });
    setIsEmailMode(true);
    setIsSSOMode(false);
  };

  const handleSSOLogin = () => {
    localStorage.setItem("lastUsedAuthnProvider", "sso");
    captureAnalyticsEvent(AnalyticsEvents.AUTH_STARTED, { provider: "sso" });
    setIsSSOMode(true);
    setIsEmailMode(false);
  };

  const handleBackToSocial = () => {
    setIsEmailMode(false);
    setIsSSOMode(false);
    setIsEmailSent(false);
    setSentEmail("");
  };

  const onSubmitEmail = async (data: EmailFormData) => {
    setIsSubmitting(true);
    try {
      // Include oauth_session in the request body if present (for MCP OAuth flow)
      const oauthSession = searchParams.get("oauth_session");
      // Credentials must be included so the browser keeps the verification
      // cookie the backend sets here. Redeeming the code needs it.
      const response = await fetch(`${import.meta.env.VITE_API_ENDPOINT}/authn/magiclink/login`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: data.email,
          ...(oauthSession && { oauth_session: oauthSession }),
        }),
      });

      if (response.ok) {
        setIsEmailSent(true);
        setSentEmail(data.email);
        toast({
          title: "Email sent!",
          description: "Open the link we emailed you, or enter the 6-digit code here.",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (errorData.code === signupDomainErrorCode) {
          setIsSignupDomainDialogOpen(true);
          return;
        }
        if (errorData.code === ssoRequiredErrorCode) {
          setIsSSOMode(true);
          setIsEmailMode(false);
          toast({ title: "SSO required", description: errorData.message });
          return;
        }
        toast({
          title: "Uh oh!",
          description: errorData.message || errorData.error || "Failed to send code. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Uh oh!",
        description: "An error occurred. Please try again. " + JSON.stringify(error),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onSubmitCode = async (data: CodeFormData) => {
    setIsSubmitting(true);
    try {
      // Redirect to magic link callback with the signup code
      window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/magiclink/callback?signup_code=${encodeURIComponent(data.code)}&${searchParams.toString()}`;
    } catch (error) {
      toast({
        title: "Uh oh!",
        description: "An error occurred. Please try again. " + JSON.stringify(error),
        variant: "destructive",
      });
      setIsSubmitting(false);
    }
  };

  const onSubmitSSO = async (data: SSOFormData) => {
    setIsSubmitting(true);
    try {
      const lookupURL = new URL(`${import.meta.env.VITE_API_ENDPOINT}/authn/oidc/lookup`);
      lookupURL.searchParams.set("email", data.email);
      const response = await fetch(lookupURL.toString());
      if (!response.ok) {
        throw new Error("Failed to check SSO configuration");
      }
      const result = await response.json() as { found: boolean };
      if (!result.found) {
        toast({
          title: "SSO not configured",
          description: "No SSO connection was found for that email domain.",
          variant: "destructive",
        });
        return;
      }
      const params = new URLSearchParams(searchParams);
      params.set("email", data.email);
      window.location.href = `${import.meta.env.VITE_API_ENDPOINT}/authn/oidc/login?${params.toString()}`;
    } catch (error) {
      toast({
        title: "Uh oh!",
        description: "An error occurred. Please try again. " + JSON.stringify(error),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const lastUsedText = <span className="text-muted-foreground text-xs">(last used)</span>;

  return (
    <PublicShell>
      <Card variant="plain" className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl">Welcome!</CardTitle>
          <CardDescription>
            {searchParams.get("signupError") === "sso_access_denied" && (
              <span role="alert" className="block mb-2">Your identity provider denied SSO sign-in. Ask your administrator to check your assignment to the Limrun application and its sign-in policies.</span>
            )}
            {searchParams.get("signupError") === "sso_provider_error" && (
              <span role="alert" className="block mb-2">Your identity provider could not complete SSO sign-in. Try again or contact your administrator.</span>
            )}
            {["sso_access_denied", "sso_provider_error"].includes(searchParams.get("signupError") ?? "") && searchParams.get("ssoError") && (
              <span className="block mb-2 whitespace-pre-wrap break-words">
                Provider error: {searchParams.get("ssoError")}
                {searchParams.get("ssoErrorDescription") && <span className="block">{searchParams.get("ssoErrorDescription")}</span>}
              </span>
            )}
            {searchParams.get("signupError") === "scim_access_denied" && (
              <span role="alert" className="block mb-2">Your organization manages access through SCIM. Ask your administrator to assign you to a Limrun access group.</span>
            )}
            {searchParams.get("signupError") === ssoRequiredErrorCode && (
              <span className="block mb-2">Your organization requires SSO. Enter your work email to continue.</span>
            )}
            {isSSOMode ? "Continue with your company SSO" : isEmailMode ? "Continue with your email" : "Choose how to continue"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isSSOMode ? (
            <div className="space-y-4">
              <Button
                variant="ghost"
                className="w-full flex items-center justify-start gap-2 p-0 h-auto"
                onClick={handleBackToSocial}
              >
                <MdArrowBack className="w-4 h-4" />
                Back to other options
              </Button>
              <form onSubmit={handleSubmitSSO(onSubmitSSO)} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="sso-email">Work email</Label>
                  <Input
                    id="sso-email"
                    type="email"
                    placeholder="you@company.com"
                    {...registerSSO("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: "Invalid email address",
                      },
                    })}
                  />
                  {ssoErrors.email && (
                    <p className="text-sm text-danger">{ssoErrors.email.message}</p>
                  )}
                </div>
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Checking..." : "Continue with SSO"}
                </Button>
              </form>
            </div>
          ) : isEmailMode ? (
            <div className="space-y-4">
              <Button
                variant="ghost"
                className="w-full flex items-center justify-start gap-2 p-0 h-auto"
                onClick={handleBackToSocial}
              >
                <MdArrowBack className="w-4 h-4" />
                Back to other options
              </Button>
              {isEmailSent ? (
                <div className="space-y-4">
                  <div className="text-center space-y-2">
                    <p className="text-sm text-muted-foreground">
                      We've sent a sign-in link and a 6-digit code to
                    </p>
                    <p className="font-medium">{sentEmail}</p>
                    <p className="text-xs text-muted-foreground">
                      Both expire in 15 minutes. Enter the code in this browser, or open the link
                      on any device.
                    </p>
                  </div>

                  <form onSubmit={handleSubmitCode(onSubmitCode)} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="code">Enter Code</Label>
                      <Input
                        id="code"
                        type="text"
                        placeholder="000000"
                        maxLength={6}
                        className="text-center text-2xl tracking-widest font-mono"
                        {...registerCode("code", {
                          required: "Code is required",
                          pattern: {
                            value: /^\d{6}$/,
                            message: "Code must be 6 digits",
                          },
                        })}
                        onChange={(e) => {
                          // Remove all non-digit characters and trim whitespace
                          const cleaned = e.target.value.replace(/\D/g, '').slice(0, 6);
                          e.target.value = cleaned;
                          // Manually trigger the react-hook-form onChange
                          registerCode("code").onChange(e);
                        }}
                        onPaste={(e) => {
                          // Handle paste event to clean the pasted content
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          const cleaned = pastedText.replace(/\D/g, '').slice(0, 6);
                          e.currentTarget.value = cleaned;
                          // Trigger react-hook-form onChange
                          registerCode("code").onChange({
                            target: { value: cleaned, name: "code" }
                          } as any);
                        }}
                        autoFocus
                      />
                      {codeErrors.code && (
                        <p className="text-sm text-danger">{codeErrors.code.message}</p>
                      )}
                    </div>
                    <Button type="submit" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? "Verifying..." : "Verify Code"}
                    </Button>
                  </form>

                  <div className="text-center space-y-2">
                    <button
                      type="button"
                      className="text-sm text-muted-foreground hover:text-foreground transition-colors underline"
                      onClick={() => {
                        setIsEmailSent(false);
                        setSentEmail("");
                      }}
                    >
                      Use a different email
                    </button>
                  </div>

                  <div className="grid gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full flex items-center justify-center gap-2"
                      onClick={() => window.open("https://mail.google.com", "_blank")}
                    >
                      <SiGmail className="w-4 h-4" /> Open Gmail
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full flex items-center justify-center gap-2"
                      onClick={() => window.open("https://outlook.live.com", "_blank")}
                    >
                      <PiMicrosoftOutlookLogoFill className="w-4 h-4" /> Open Outlook
                    </Button>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit(onSubmitEmail)} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email address"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: "Invalid email address",
                        },
                      })}
                    />
                    {errors.email && (
                      <p className="text-sm text-danger">{errors.email.message}</p>
                    )}
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? "Sending..." : "Send verification code"}
                  </Button>
                </form>
              )}
            </div>
          ) : (
            <div className="grid gap-4">
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleGoogleLogin}>
                <span className="flex items-center gap-2">
                  <FcGoogle className="w-5 h-5" />
                  Continue with Google
                </span>
                {lastUsedAuthnProvider === "google" ? lastUsedText : ""}
              </Button>
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleGitHubLogin}>
                <span className="flex items-center gap-2">
                  <FaGithub className="w-5 h-5" />
                  Continue with Github
                </span>
                {lastUsedAuthnProvider === "github" ? lastUsedText : ""}
              </Button>
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleAgentIDLogin}>
                <span className="flex items-center gap-2">
                  <img src="/agentid.svg" alt="" className="w-5 h-5 dark:invert" />
                  Continue with AgentID
                </span>
                {lastUsedAuthnProvider === "agentid" ? lastUsedText : ""}
              </Button>
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleEmailLogin}>
                <span className="flex items-center gap-2">
                  <MdEmail className="w-5 h-5" />
                  Continue with Email
                </span>
                {lastUsedAuthnProvider === "email" ? lastUsedText : ""}
              </Button>
              {/* Separator between social options and email */}
              <div className="h-px w-full bg-muted" />
              <Button variant="outline" className="w-full flex justify-between items-center" onClick={handleSSOLogin}>
                <span className="flex items-center gap-2">
                  <MdBusiness className="w-5 h-5" />
                  Continue with SSO
                </span>
                {lastUsedAuthnProvider === "sso" ? lastUsedText : ""}
              </Button>
            </div>
          )}
          <p className="mt-6 text-center text-xs text-muted-foreground">
            By continuing, you agree to our{" "}
            <a href="https://limrun.com/terms" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              Terms of Service
            </a>{" "}
            and{" "}
            <a href="https://limrun.com/privacy" target="_blank" rel="noopener noreferrer" className="underline hover:text-foreground">
              Privacy Policy
            </a>
            .
          </p>
        </CardContent>
      </Card>
      <Dialog open={isSignupDomainDialogOpen} onOpenChange={setIsSignupDomainDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invitation</DialogTitle>
            <DialogDescription>
              Due to high demand, Limrun is currently available by invitation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button className="w-full sm:w-auto" asChild>
              <a
                href={DEMO_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleConsoleDemoNavigation}
                onAuxClick={handleConsoleDemoNavigation}
                onContextMenu={(event) => prepareConsoleDemoLink(event.currentTarget)}
              >
                Request a demo
              </a>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PublicShell>
  );
}
